import { isRequestTimeoutError } from './ApiService';

/**
 * T-0559: a launch screen's first data, asked for at boot beside the session check rather than one
 * round trip after it.
 *
 * The read is started once, by `bootRoutesFor` in `App.tsx`, for the route the address lands on.
 * The first caller that asks for the same thing within `BOOT_PREFETCH_MAX_AGE_MS` is handed that read,
 * and only the first: every later read reaches the server, so nothing goes stale by this. A
 * prefetch that failed (a session the server no longer accepts, a network drop) is not handed out:
 * the caller asks again, as it would have. One that ran out the request deadline is: it already had
 * the whole wait a read gets, and asking again doubled it — a server that never answers took 40 s
 * to say so on the Decisions tab, not 20 (gate 474 sweep, decisions-stuck-phone RD-12).
 */
export const BOOT_PREFETCH_MAX_AGE_MS = 10_000;

const entries = new Map<string, { read: Promise<unknown>; startedAt: number }>();

export function prefetchAtBoot(
  key: string,
  read: () => Promise<unknown>,
  now: () => number = Date.now,
): void {
  const started = read();
  // Nobody may ever take it; its failure is the taker's to handle, never an unhandled rejection.
  started.catch(() => undefined);
  entries.set(key, { read: started, startedAt: now() });
}

export function takeBootPrefetch<T>(
  key: string,
  read: () => Promise<T>,
  now: () => number = Date.now,
): Promise<T> {
  const entry = entries.get(key);
  entries.delete(key);
  if (!entry || now() - entry.startedAt >= BOOT_PREFETCH_MAX_AGE_MS) return read();
  return (entry.read as Promise<T>).catch((error: unknown) =>
    isRequestTimeoutError(error) ? Promise.reject(error) : read(),
  );
}

export function resetBootPrefetchForTests(): void {
  entries.clear();
}
